import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { selectWhatsNewVersions, selectWhatsNewChangelogUrl } from 'store/WhatsNew/selectors';
import { selectWhatsNewSeenReleases } from 'store/Preferences/whatsNew/selectors';
import { markWhatsNewReleasesSeen } from 'store/Preferences/whatsNew/actions';
import { buildSeenReleases } from 'store/Preferences/whatsNew/helpers';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import BlueButton from 'Common/Components/BlueButton';
import WhatsNewTab from './WhatsNewTab';
import WhatsNewItem from './WhatsNewItem';
import {
  Wrapper, CloseIcon, Header, HeaderIconBox, HeaderIcon, HeaderTextColumn, PrimaryTitle, Subtitle, TabBar, ItemList, Footer, ChangelogLink,
  ActionIcon, HeaderContent
} from './styled';

const WhatsNew = ({ onClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const workspace = useSelector(selectCurrentWorkspace);
  const storedSeenReleases = useSelector(selectWhatsNewSeenReleases);

  const versions = useMemo(() => selectWhatsNewVersions(), []);
  const changelogUrl = useMemo(() => selectWhatsNewChangelogUrl(), []);
  const versionIds = useMemo(() => versions.map((version) => version.id), [versions]);
  const seenReleases = useMemo(
    () => buildSeenReleases(versionIds, storedSeenReleases),
    [versionIds, storedSeenReleases]
  );

  const [activeVersionId, setActiveVersionId] = useState(versions[0]?.id);
  const activeVersion = versions.find((version) => version.id === activeVersionId);
  const whatsNewBadgeLabel = intl.formatMessage({ id: 'user.menu.whatsNew.badge' });

  const seenVersionIdsRef = useRef(new Set());
  useEffect(() => {
    if (activeVersionId) seenVersionIdsRef.current.add(activeVersionId);
  }, [activeVersionId]);

  useEffect(() => () => {
    if (seenVersionIdsRef.current.size > 0) {
      dispatch(markWhatsNewReleasesSeen(versionIds, Array.from(seenVersionIdsRef.current)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onQuickTour = (route) => {
    onClose();
    dispatch(push(workspace ? route.replace(':workspace', workspace) : route));
  };

  if (!activeVersion) return null;

  return (
    <Wrapper maxWidth="560px">
      <Header>

      <HeaderContent>
        <HeaderIconBox>
          <HeaderIcon />
        </HeaderIconBox>
        <HeaderTextColumn>
          <PrimaryTitle>{intl.formatMessage({ id: 'user.whatsNew.title' })}</PrimaryTitle>
          <Subtitle>
            {intl.formatMessage(
              { id: 'user.whatsNew.subtitle' },
              { version: activeVersion.label, date: moment(activeVersion.releaseDate).format('LL') }
            )}
          </Subtitle>
        </HeaderTextColumn>
      </HeaderContent>
            <CloseIcon onClick={ onClose } />

      </Header>

      <TabBar>
        {versions.map((version, index) => (
          <WhatsNewTab
            key={ version.id }
            label={ version.label }
            active={ version.id === activeVersionId }
            unseen={ seenReleases.find((release) => release.version === version.id)?.seen === false }
            isLatest={ index === 0 }
            badgeLabel={ whatsNewBadgeLabel }
            onClick={ () => setActiveVersionId(version.id) }
          />
        ))}
      </TabBar>

      <ItemList>
        {activeVersion.items.map((item) => (
          <WhatsNewItem key={ item.id } item={ item } onQuickTour={ onQuickTour } />
        ))}
      </ItemList>

      <Footer>
        <ChangelogLink href={ changelogUrl } target="_blank" rel="noopener noreferrer">
          {intl.formatMessage({ id: 'user.whatsNew.changelogLink' })}
          <ActionIcon />
        </ChangelogLink>
        <BlueButton onClick={ onClose }>
          {intl.formatMessage({ id: 'user.whatsNew.gotIt' })}
        </BlueButton>
      </Footer>
    </Wrapper>
  );
};

WhatsNew.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default WhatsNew;
